import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './PropertiesPage.css';

const API_BASE = process.env.REACT_APP_API_BASE;

function getToken() {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}
function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}
function getUser() {
  const t = getToken();
  if (!t) return null;
  try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; }
}

// ── Icons ──────────────────────────────────────────────────────────────────────

function PlusIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function EditIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function CloseIcon()  { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function SearchIcon() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function HomeIcon()   { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>; }
function BookIcon()     { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>; }
function ExternalIcon() { return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>; }
function CopyIcon()     { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>; }

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
  } catch { return iso; }
}

// ── Modal wrapper ──────────────────────────────────────────────────────────────

function Modal({ onClose, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="pr-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pr-modal" style={{ maxWidth: 520 }}>
        {children}
      </div>
    </div>
  );
}

// ── Toast ──────────────────────────────────────────────────────────────────────

function Toast({ msg, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className="pr-toast">{msg}</div>;
}

// ── Guidebook detail popup ─────────────────────────────────────────────────────

function GuidebookDetailModal({ g, onClose }) {
  const [copied, setCopied] = useState(false);

  function copyUrl() {
    navigator.clipboard.writeText(g.guide_chatbot_url || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Modal onClose={onClose}>
      <div className="pr-modal-head">
        <div className="pr-gb-modal-title">
          <span className="pr-gb-modal-icon"><BookIcon /></span>
          <h3>{g.guidebook_title}</h3>
        </div>
        <button className="pr-modal-close" onClick={onClose}><CloseIcon /></button>
      </div>
      <div className="pr-modal-body">

        <div className="pr-gb-detail-section">
          <div className="pr-gb-detail-label">Chatbot URL</div>
          <div className="pr-gb-detail-url-row">
            <span className="pr-gb-detail-url">{g.guide_chatbot_url || '—'}</span>
            {g.guide_chatbot_url && (
              <div className="pr-gb-detail-url-btns">
                <a href={g.guide_chatbot_url} target="_blank" rel="noreferrer" className="pr-gb-open-btn">
                  <ExternalIcon /> Open
                </a>
                <button className="pr-gb-copy-btn" onClick={copyUrl}>
                  <CopyIcon /> {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>

        {g.chatbot_description && (
          <div className="pr-gb-detail-section">
            <div className="pr-gb-detail-label">Description</div>
            <div className="pr-gb-detail-value">{g.chatbot_description}</div>
          </div>
        )}

        {g.cards?.length > 0 && (
          <div className="pr-gb-detail-section">
            <div className="pr-gb-detail-label">Quick Action Cards ({g.cards.length})</div>
            <div className="pr-gb-cards-wrap">
              {g.cards.map((c, i) => (
                <span key={i} className="pr-gb-card-pill">
                  {c.card_icon} {c.card_label}
                </span>
              ))}
            </div>
          </div>
        )}

        {g.guide_original_url && (
          <div className="pr-gb-detail-section">
            <div className="pr-gb-detail-label">Original Guide URL</div>
            <a href={g.guide_original_url} target="_blank" rel="noreferrer" className="pr-gb-orig-link">
              {g.guide_original_url} <ExternalIcon />
            </a>
          </div>
        )}

        <div className="pr-modal-foot">
          <button className="pr-btn pr-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Add / Edit modal ───────────────────────────────────────────────────────────

function PropertyModal({ title, initial, managers, isAdmin, onClose, onSave }) {
  const [form, setForm]   = useState({ property_address: initial?.property_address || '', manager_id: initial?.manager_id || '' });
  const [err, setErr]     = useState('');
  const [apiErr, setApiErr] = useState('');
  const [busy, setBusy]   = useState(false);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); setErr(''); setApiErr(''); }

  async function handleSave() {
    const addr = form.property_address.trim();
    if (!addr) { setErr('Property address is required.'); return; }
    setBusy(true);
    setApiErr('');
    try {
      await onSave({ property_address: addr, manager_id: form.manager_id || null });
    } catch (e) {
      setApiErr(e.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className="pr-modal-head">
        <h3>{title}</h3>
        <button className="pr-modal-close" onClick={onClose}><CloseIcon /></button>
      </div>
      <div className="pr-modal-body">
        {apiErr && <div className="pr-api-err">{apiErr}</div>}

        <div className="pr-field">
          <label className="pr-label">Property Address <span className="pr-req">*</span></label>
          <textarea
            className={`pr-textarea${err ? ' pr-input--err' : ''}`}
            rows={4}
            placeholder="Enter full property address..."
            value={form.property_address}
            onChange={e => set('property_address', e.target.value)}
          />
          {err && <span className="pr-field-err">{err}</span>}
        </div>

        {isAdmin && (
          <div className="pr-field">
            <label className="pr-label">Assign to Manager</label>
            <select className="pr-select" value={form.manager_id} onChange={e => set('manager_id', e.target.value)}>
              <option value="">— Unassigned —</option>
              {managers.map(m => (
                <option key={m.manager_id} value={m.manager_id}>{m.manager_name} ({m.email})</option>
              ))}
            </select>
            <p className="pr-hint">Optional — leave blank to create unassigned.</p>
          </div>
        )}

        <div className="pr-modal-foot">
          <button className="pr-btn pr-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="pr-btn pr-btn-primary" onClick={handleSave} disabled={busy}>
            {busy ? <span className="pr-spinner" /> : null}
            {busy ? 'Saving…' : 'Save Property'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function PropertiesPage() {
  const user      = useMemo(() => getUser(), []);
  const isAdmin   = user?.user_type === 'admin';

  const [props,    setProps]    = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [fetchErr, setFetchErr] = useState('');
  const [search,   setSearch]   = useState('');
  const [modal,    setModal]    = useState(null); // null | { mode:'add' } | { mode:'edit', prop }
  const [toast,    setToast]    = useState('');
  const [gbModal,  setGbModal]  = useState(null); // full guidebook detail for popup
  const [gbBusy,   setGbBusy]   = useState(null); // guideid being fetched

  const showToast = useCallback((msg) => { setToast(msg); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchErr('');
    try {
      const [pRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/api/properties`, { headers: authHeaders() }),
        isAdmin ? fetch(`${API_BASE}/api/properties/managers-list`, { headers: authHeaders() }) : Promise.resolve(null),
      ]);

      if (!pRes.ok) {
        const d = await pRes.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to load properties.');
      }
      setProps(await pRes.json());

      if (mRes && mRes.ok) {
        setManagers(await mRes.json());
      }
    } catch (e) {
      setFetchErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return props;
    return props.filter(p =>
      p.property_address?.toLowerCase().includes(q) ||
      p.manager_name?.toLowerCase().includes(q)
    );
  }, [props, search]);

  async function openGbModal(guideid) {
    setGbBusy(guideid);
    try {
      const res = await fetch(`${API_BASE}/api/guidebooks/${guideid}`, { headers: authHeaders() });
      if (res.ok) setGbModal(await res.json());
    } catch { /* ignore */ }
    setGbBusy(null);
  }

  async function handleAdd(data) {
    const res = await fetch(`${API_BASE}/api/properties`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || 'Failed to create property.');
    setModal(null);
    showToast('Property registered successfully.');
    load();
  }

  async function handleEdit(data) {
    const { propId } = modal.prop;
    const res = await fetch(`${API_BASE}/api/properties/${propId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || 'Failed to update property.');
    setModal(null);
    showToast('Property updated successfully.');
    load();
  }

  return (
    <div className="pr-page">

      {/* ── Header ── */}
      <div className="pr-page-head">
        <div className="pr-summary">
          <span className="pr-chip pr-chip-total">
            <HomeIcon />
            <strong>{props.length}</strong> {props.length === 1 ? 'Property' : 'Properties'}
            {!isAdmin && ' (yours)'}
          </span>
        </div>
        <button className="pr-add-btn" onClick={() => setModal({ mode: 'add' })}>
          <PlusIcon /> Add Property
        </button>
      </div>

      {/* ── Search ── */}
      <div className="pr-search-bar">
        <span className="pr-search-icon"><SearchIcon /></span>
        <input
          className="pr-search-input"
          type="text"
          placeholder="Search by address or manager…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className="pr-search-clear" onClick={() => setSearch('')}><CloseIcon /></button>
        )}
      </div>

      {fetchErr && <div className="pr-alert">{fetchErr}</div>}

      {/* ── Table ── */}
      <div className="pr-table-card">
        <div className="pr-table-wrap">
          {loading ? (
            <div className="pr-skeleton-rows">
              {[...Array(5)].map((_, i) => (
                <div className="pr-skeleton-row" key={i}>
                  <div className="pr-sk" style={{ width: '40%' }} />
                  <div className="pr-sk" style={{ width: '18%' }} />
                  <div className="pr-sk" style={{ width: '14%' }} />
                  <div className="pr-sk" style={{ width: '60px' }} />
                </div>
              ))}
            </div>
          ) : (
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Property Address</th>
                  <th>Guidebook</th>
                  {isAdmin && <th>Manager</th>}
                  <th>Created</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 6 : 5} className="pr-empty">
                      {search ? 'No properties match your search.' : 'No properties registered yet.'}
                    </td>
                  </tr>
                ) : filtered.map(p => (
                  <tr key={p.propId}>
                    <td>
                      <div className="pr-addr-cell">
                        <span className="pr-addr-icon"><HomeIcon /></span>
                        <span className="pr-addr-text">{p.property_address}</span>
                      </div>
                    </td>
                    <td className="pr-td-guidebook">
                      {!p.guidebooks?.length
                        ? <span className="pr-gb-none">—</span>
                        : <div className="pr-gb-list">
                            {p.guidebooks.map(gb => (
                              <button
                                key={gb.guideid}
                                className="pr-gb-link"
                                onClick={() => openGbModal(gb.guideid)}
                                disabled={gbBusy === gb.guideid}
                                title="View guidebook details"
                              >
                                {gbBusy === gb.guideid
                                  ? <span className="pr-gb-spinner" />
                                  : <BookIcon />
                                }
                                {gb.guidebook_title}
                              </button>
                            ))}
                          </div>
                      }
                    </td>
                    {isAdmin && (
                      <td>
                        {p.manager_name
                          ? <span className="pr-manager-tag">{p.manager_name}</span>
                          : <span className="pr-unassigned">Unassigned</span>}
                      </td>
                    )}
                    <td className="pr-td-date">
                      <div>{fmtDate(p.created_date)}</div>
                      {p.created_by && <div className="pr-td-by">{p.created_by}</div>}
                    </td>
                    <td className="pr-td-date">
                      <div>{fmtDate(p.modified_date)}</div>
                      {p.modified_by && <div className="pr-td-by">{p.modified_by}</div>}
                    </td>
                    <td>
                      <div className="pr-actions">
                        <button
                          className="pr-icon-btn pr-icon-edit"
                          title="Edit property"
                          onClick={() => setModal({ mode: 'edit', prop: p })}
                        >
                          <EditIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!loading && filtered.length > 0 && (
          <div className="pr-table-foot">
            Showing {filtered.length} of {props.length} {props.length === 1 ? 'property' : 'properties'}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {modal?.mode === 'add' && (
        <PropertyModal
          title="Register New Property"
          initial={null}
          managers={managers}
          isAdmin={isAdmin}
          onClose={() => setModal(null)}
          onSave={handleAdd}
        />
      )}
      {modal?.mode === 'edit' && (
        <PropertyModal
          title="Edit Property"
          initial={modal.prop}
          managers={managers}
          isAdmin={isAdmin}
          onClose={() => setModal(null)}
          onSave={handleEdit}
        />
      )}

      {/* ── Guidebook detail popup ── */}
      {gbModal && <GuidebookDetailModal g={gbModal} onClose={() => setGbModal(null)} />}

      {/* ── Toast ── */}
      {toast && <Toast msg={toast} onDone={() => setToast('')} />}
    </div>
  );
}
